import { useAuth } from '@clerk/nextjs'
import { useEffect, useState } from 'react'
import { type UserRole } from '@/database/schema'

interface UserPermissions {
  isLoading: boolean
  isAuthenticated: boolean
  userRoles: UserRole[] | null
  canAccessDashboard: boolean
  canAccessAdmin: boolean
  canAccessUser: boolean
  getHighestPriorityPanelUrl: () => string
  error: string | null
}

export function useUserPermissions(): UserPermissions {
  const { userId, isLoaded } = useAuth()

  const getHighestPriorityPanelUrl = (roles: UserRole[] | null): string => {
    if (!roles || !roles.length) {
      return '/user' // Default fallback
    }

    // Check roles in priority order
    if (roles.includes('admin')) {
      return '/admin'
    }

    if (roles.includes('osk_owner')) {
      return '/dashboard'
    }

    // All other roles go to user panel
    return '/user'
  }

  const [permissions, setPermissions] = useState<UserPermissions>({
    isLoading: true,
    isAuthenticated: false,
    userRoles: null,
    canAccessDashboard: false,
    canAccessAdmin: false,
    canAccessUser: false,
    getHighestPriorityPanelUrl: () => getHighestPriorityPanelUrl(null),
    error: null,
  })

  useEffect(() => {
    if (!isLoaded) return

    if (!userId) {
      setPermissions({
        isLoading: false,
        isAuthenticated: false,
        userRoles: null,
        canAccessDashboard: false,
        canAccessAdmin: false,
        canAccessUser: false,
        getHighestPriorityPanelUrl: () => getHighestPriorityPanelUrl(null),
        error: null,
      })
      return
    }

    // Fetch user role from our database
    const fetchUserRole = async () => {
      try {
        const response = await fetch('/api/auth/user-role')

        if (!response.ok) {
          throw new Error('Failed to fetch user role')
        }

        const { role } = await response.json()

        setPermissions({
          isLoading: false,
          isAuthenticated: true,
          userRoles: role,
          canAccessDashboard: role.includes('osk_owner'),
          canAccessAdmin: role.includes('admin'),
          canAccessUser: true, // Any authenticated user can access user pages
          getHighestPriorityPanelUrl: () => getHighestPriorityPanelUrl(role),
          error: null,
        })
      } catch (error) {
        setPermissions({
          isLoading: false,
          isAuthenticated: true,
          userRoles: null,
          canAccessDashboard: false,
          canAccessAdmin: false,
          canAccessUser: false,
          getHighestPriorityPanelUrl: () => getHighestPriorityPanelUrl(null),
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    }

    fetchUserRole()
  }, [userId, isLoaded])

  return permissions
}
